var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * CZNA-1034 — "is this person already in the base?", asked while the cataloguer
 * is still typing.
 *
 * <p>NK asked for this after losing to the race it describes: two cataloguers
 * create the same authority and one saves first (P. Šťastná, 7. 9. 2026). The
 * existing `DUPLICATE_HEADING` warning cannot answer it — it matches the fully
 * composed heading, so `Ondroušek, Antonín,` warns about nothing while two such
 * authorities exist under different dates (measured live, see
 * {@link headingNameQuery}). And a record with uncertain dates — the case she
 * was actually testing — can never collide on the composed form at all.
 *
 * <p>Automatic rather than a button the cataloguer presses. Measured over the
 * live corpus, 90.1 % of active PERSONAL records carry a `100 $a` no other
 * record shares, so the hint is silent for nine records out of ten and shows
 * one to four candidates when it speaks. A check you have to remember to run is
 * a check that does not run in the one situation that needs it: nobody suspects
 * a duplicate at the moment they are creating one.
 *
 * <p>No new endpoint. The composed heading BEGINS with the name, so a prefix
 * query over heading text returns the namesakes, and the search already folds
 * diacritics and covers VARIANT headings — which is how `Barnabáš` finds the
 * existing `Barnabus` through its `Barnabas` variant.
 */
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useOkapiKy } from '@folio/stripes/core';
import { fetchRecords } from '../api/recordService';
import { fetchProposals } from '../api/proposalService';
import { ProposalStatus } from '../types/api';
export var SIMILAR_HEADINGS_QUERY_KEY = 'czna-similar-headings';
/**
 * Ceiling on what the hint lists.
 *
 * <p>The tail is real: `Novák, Jan` is 205 active records, `Novák, Josef,` 46
 * (measured 7. 9. 2026). Listing them would bury the answer, so the hint shows
 * the first few and says how many there are — the register is where you go to
 * read all of them.
 */
export var SIMILAR_HEADINGS_LIMIT = 8;
/**
 * How long the name has to sit still before it is asked about.
 *
 * <p>Without this the query key changes on every character, so "Ondroušek,
 * Antonín" is eighteen round trips — times three, since proposals are searched
 * as well. `MarcPreviewPanel` debounces its own recompute for the same reason.
 */
export var SIMILAR_HEADINGS_DEBOUNCE_MS = 400;
/**
 * Proposal states that can still collide.
 *
 * <p>The race NK described is between two people CREATING the same authority —
 * "jednomu se povede uložit ji dříve" — so a draft nobody has published yet is
 * the case, and a hint that searched only published records would miss it.
 * `PENDING_REVIEW` counts too: it is still unpublished, and its author can no
 * longer change it, which makes knowing about it more useful rather than less.
 *
 * <p>Terminal states are deliberately absent. `REJECTED` collides with nothing
 * and `APPROVED` is already a published record, so it would be counted twice.
 */
var COLLIDING_PROPOSAL_STATUSES = [
    ProposalStatus.DRAFT,
    ProposalStatus.PENDING_REVIEW,
];
/**
 * @param recordType the authority type being edited — namesakes are only
 *                   namesakes within the same type
 * @param nameQuery  normalised name prefix from `headingNameQuery`, or `null` to
 *                   ask nothing
 * @param exclusions the record / proposal being edited, so it is not reported
 *                   about itself
 */
export var useSimilarHeadings = function (recordType, nameQuery, exclusions) {
    if (exclusions === void 0) { exclusions = {}; }
    var ky = useOkapiKy();
    var excludeAuthorityId = exclusions.excludeAuthorityId, excludeProposalId = exclusions.excludeProposalId;
    // The name the query is actually keyed on: the typed one, once it stops moving.
    var _a = useState(null), settledName = _a[0], setSettledName = _a[1];
    useEffect(function () {
        var timer = setTimeout(function () { return setSettledName(nameQuery); }, SIMILAR_HEADINGS_DEBOUNCE_MS);
        return function () { return clearTimeout(timer); };
    }, [nameQuery]);
    return useQuery(
    // Exclusions are deliberately NOT in the key: they change what is filtered
    // out, not what was asked, and keying on them would re-fetch identical
    // results per edited record.
    [SIMILAR_HEADINGS_QUERY_KEY, recordType, settledName], function () { return __awaiter(void 0, void 0, void 0, function () {
        var name, _a, records, proposalPages, recordItems, proposalItems, items, fetchedCount, reportedTotal, removed, total;
        var _b, _c, _d, _e;
        return __generator(this, function (_f) {
            switch (_f.label) {
                case 0:
                    name = settledName;
                    return [4 /*yield*/, Promise.all(__spreadArray([
                            fetchRecords(ky, {
                                query: name,
                                searchMode: 'STARTS_WITH',
                                type: recordType,
                                status: 'active',
                                sortBy: 'headingValue',
                                sortOrder: 'asc',
                                limit: SIMILAR_HEADINGS_LIMIT,
                                includeTotalCount: true,
                                // CZNA-1648 — a namesake check, not a search: Kyša is no namesake of his pseudonym
                                // Kotleta, though the search finds him through his 500 link.
                                includeRelated: false,
                            })
                        ], COLLIDING_PROPOSAL_STATUSES.map(function (status) { return fetchProposals(ky, {
                            status: status,
                            recordType: recordType,
                            // The proposals endpoint searches `heading` as a case-insensitive
                            // SUBSTRING and takes no searchMode, so this is broader than the
                            // record side rather than narrower — acceptable for a courtesy hint,
                            // and cheap: proposals are thousands of rows, not 1.27 M.
                            heading: name,
                            includeTotalCount: true,
                            limit: SIMILAR_HEADINGS_LIMIT,
                        }); }), true))];
                case 1:
                    _a = _f.sent(), records = _a[0], proposalPages = _a.slice(1);
                    recordItems = ((_b = records.items) !== null && _b !== void 0 ? _b : [])
                        .filter(function (r) { return r.authorityId !== excludeAuthorityId && r.id !== excludeAuthorityId; })
                        .map(function (r) { return ({
                        id: r.id,
                        headingValue: r.headingValue,
                        matchedHeading: r.matchedHeading,
                        kind: 'record',
                    }); });
                    proposalItems = proposalPages
                        .flatMap(function (page) { var _a; return (_a = page.items) !== null && _a !== void 0 ? _a : []; })
                        .filter(function (p) { return p.id !== excludeProposalId; })
                        .map(function (p) { return ({ id: p.id, headingValue: p.headingValue, kind: 'proposal' }); });
                    items = __spreadArray(__spreadArray([], recordItems, true), proposalItems, true);
                    fetchedCount = ((_d = (_c = records.items) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0)
                        + proposalPages.reduce(function (sum, page) { var _a, _b; return sum + ((_b = (_a = page.items) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0); }, 0);
                    reportedTotal = ((_e = records.totalRecords) !== null && _e !== void 0 ? _e : 0)
                        + proposalPages.reduce(function (sum, page) { var _a; return sum + ((_a = page.totalRecords) !== null && _a !== void 0 ? _a : 0); }, 0);
                    removed = fetchedCount - items.length;
                    total = Math.max(reportedTotal - removed, items.length);
                    return [2 /*return*/, {
                            items: items,
                            total: total,
                            capped: records.totalRecordsCapped === true,
                        }];
            }
        });
    }); }, {
        enabled: settledName !== null,
        // A name does not become a different name while the form is open, and the
        // hint is advisory — half a minute stale is fine, a request per keystroke
        // is not.
        staleTime: 30000,
        // Never surface a failure: this is a courtesy, and a red banner because a
        // background lookup timed out would be worse than no hint at all.
        retry: false,
    });
};
